import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Package, MapPin, CreditCard, User as UserIcon, Truck, CheckCircle2, Circle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

export default function MyAccount() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "profile";

  useEffect(() => {
    if (!loading && !user) navigate("/signin");
  }, [user, loading, navigate]);

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16" data-testid="account-page">
      <div className="mb-10">
        <p className="text-sm uppercase tracking-[0.2em] text-[#E65A28] mb-2 font-semibold">Namaste, {user.name?.split(" ")[0]}</p>
        <h1 className="font-serif text-5xl text-[#0A192F]">My Account</h1>
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="bg-white rounded-full p-1 h-auto flex-wrap">
          <TabsTrigger value="profile" className="rounded-full px-5 py-2" data-testid="tab-profile"><UserIcon className="h-4 w-4 mr-2" />Profile</TabsTrigger>
          <TabsTrigger value="orders" className="rounded-full px-5 py-2" data-testid="tab-orders"><Package className="h-4 w-4 mr-2" />Orders</TabsTrigger>
          <TabsTrigger value="addresses" className="rounded-full px-5 py-2" data-testid="tab-addresses"><MapPin className="h-4 w-4 mr-2" />Addresses</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-full px-5 py-2" data-testid="tab-payments"><CreditCard className="h-4 w-4 mr-2" />Payments</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-8"><ProfileTab user={user} /></TabsContent>
        <TabsContent value="orders" className="mt-8"><OrdersTab /></TabsContent>
        <TabsContent value="addresses" className="mt-8"><AddressesTab /></TabsContent>
        <TabsContent value="payments" className="mt-8"><PaymentsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function ProfileTab({ user }) {
  return (
    <div className="bg-white rounded-2xl p-8 max-w-2xl">
      <div className="flex items-center gap-4 mb-6">
        {user.picture && <img src={user.picture} alt="" className="h-16 w-16 rounded-full object-cover" />}
        <div>
          <p className="font-serif text-2xl text-[#0A192F]">{user.name}</p>
          <p className="text-sm text-[#666]">{user.email}</p>
        </div>
      </div>
      <div className="space-y-4">
        <div><Label className="text-xs uppercase tracking-widest text-[#666]">Full name</Label><Input value={user.name || ""} readOnly className="mt-1 bg-[#F9F6F0]" /></div>
        <div><Label className="text-xs uppercase tracking-widest text-[#666]">Email</Label><Input value={user.email} readOnly className="mt-1 bg-[#F9F6F0]" /></div>
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [tracking, setTracking] = useState(null);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data));
  }, []);

  const openTrack = async (orderId) => {
    const r = await api.get(`/orders/${orderId}/tracking`);
    setTracking(r.data);
  };

  if (orders.length === 0) {
    return <div className="bg-white rounded-2xl p-10 text-center text-[#666]">No orders yet. Start shopping!</div>;
  }
  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.order_id} className="bg-white rounded-2xl p-6" data-testid={`order-${o.order_id}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-[#666]">Order ID</p>
              <p className="font-semibold text-[#0A192F]">{o.order_id}</p>
              <p className="text-xs text-[#666] mt-1">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
            </div>
            <div className="text-right">
              <span className="inline-block bg-[#F4EBE1] text-[#0A192F] text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full">
                {o.order_status.replace(/_/g, " ")}
              </span>
              <p className="mt-2 text-lg font-semibold text-[#0A192F]">₹{o.total.toFixed(0)}</p>
            </div>
          </div>
          <div className="mt-4 flex gap-3 overflow-x-auto">
            {o.items.map((i) => (
              <img key={i.product_id} src={abs(i.image_url)} alt="" className="h-16 w-16 rounded-lg object-contain bg-[#F4EBE1] flex-shrink-0 p-1" />
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button onClick={() => openTrack(o.order_id)} className="text-sm bg-[#0A192F] text-white px-4 py-2 rounded-full hover:bg-[#E65A28] transition-colors" data-testid={`track-${o.order_id}`}><Truck className="h-3 w-3 inline mr-1" /> Track order</button>
          </div>
          {tracking && tracking.order_id === o.order_id && (
            <div className="mt-6 border-t border-[#0A192F]/10 pt-6">
              <p className="text-sm text-[#666] mb-4">Tracking ID: <span className="font-semibold text-[#0A192F]">{tracking.tracking_id}</span> • Carrier: Delhivery</p>
              <div className="space-y-3">
                {tracking.stages.map((s, idx) => {
                  const stageIdx = tracking.stages.findIndex(st => st.key === tracking.current_stage);
                  const done = idx <= stageIdx;
                  return (
                    <div key={s.key} className="flex items-start gap-3">
                      {done ? <CheckCircle2 className="h-5 w-5 text-[#E65A28] flex-shrink-0" /> : <Circle className="h-5 w-5 text-[#ccc] flex-shrink-0" />}
                      <div>
                        <p className={`font-medium ${done ? "text-[#0A192F]" : "text-[#999]"}`}>{s.label}</p>
                        <p className="text-xs text-[#666]">{new Date(s.at).toLocaleString("en-IN")}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AddressesTab() {
  const [addresses, setAddresses] = useState([]);
  const [form, setForm] = useState({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", is_default: false });
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => api.get("/addresses").then((r) => setAddresses(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.full_name || !form.phone || !form.line1 || !form.city || !form.state || !form.pincode) {
      toast.error("Please fill all fields"); return;
    }
    await api.post("/addresses", form);
    toast.success("Address saved");
    setForm({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", is_default: false });
    setShowForm(false);
    load();
  };
  const del = async (id) => { await api.delete(`/addresses/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {addresses.map((a) => (
          <div key={a.address_id} className="bg-white rounded-2xl p-6" data-testid={`address-${a.address_id}`}>
            {a.is_default && <span className="inline-block bg-[#E65A28] text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full mb-2">Default</span>}
            <p className="font-semibold text-[#0A192F]">{a.full_name}</p>
            <p className="text-sm text-[#666] mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
            <p className="text-sm text-[#666]">{a.city}, {a.state} - {a.pincode}</p>
            <p className="text-sm text-[#666] mt-1">📞 {a.phone}</p>
            <button onClick={() => del(a.address_id)} className="mt-3 text-xs text-[#E65A28] hover:underline" data-testid={`delete-address-${a.address_id}`}>Remove</button>
          </div>
        ))}
      </div>

      {showForm ? (
        <div className="bg-white rounded-2xl p-6" data-testid="new-address-form">
          <h3 className="font-serif text-xl text-[#0A192F] mb-4">New Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[["full_name","Full name","md:col-span-2"],["phone","Phone",""],["pincode","Pincode",""],["line1","Address line 1","md:col-span-2"],["line2","Address line 2 (optional)","md:col-span-2"],["city","City",""],["state","State",""]].map(([k,l,c])=>(
              <div key={k} className={c}>
                <Label className="text-xs uppercase tracking-widest text-[#666]">{l}</Label>
                <Input value={form[k]} onChange={(e) => setForm({...form, [k]: e.target.value})} className="mt-1" data-testid={`new-addr-${k}`} />
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-3">
            <button onClick={save} className="bg-[#E65A28] text-white px-6 py-2 rounded-full" data-testid="save-address-btn">Save address</button>
            <button onClick={() => setShowForm(false)} className="border border-[#0A192F] text-[#0A192F] px-6 py-2 rounded-full">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowForm(true)} className="bg-[#0A192F] text-white px-6 py-3 rounded-full font-semibold" data-testid="add-address-btn">+ Add new address</button>
      )}
    </div>
  );
}

function PaymentsTab() {
  const [pms, setPms] = useState([]);
  const [form, setForm] = useState({ method_type: "upi", label: "", details: "" });
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => api.get("/payment-methods").then((r) => setPms(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.label || !form.details) { toast.error("Fill all fields"); return; }
    await api.post("/payment-methods", form);
    toast.success("Saved");
    setForm({ method_type: "upi", label: "", details: "" });
    setShowForm(false);
    load();
  };
  const del = async (id) => { await api.delete(`/payment-methods/${id}`); load(); };

  return (
    <div className="space-y-4">
      <p className="text-sm text-[#666]">Save your preferred payment options for faster checkout. Actual payments are securely handled by PhonePe Business.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {pms.map((pm) => (
          <div key={pm.pm_id} className="bg-white rounded-2xl p-6" data-testid={`pm-${pm.pm_id}`}>
            <p className="text-xs uppercase tracking-widest text-[#E65A28] font-semibold">{pm.method_type}</p>
            <p className="font-semibold text-[#0A192F] mt-1">{pm.label}</p>
            <p className="text-sm text-[#666]">{pm.details}</p>
            <button onClick={() => del(pm.pm_id)} className="mt-3 text-xs text-[#E65A28] hover:underline">Remove</button>
          </div>
        ))}
      </div>

      {showForm ? (
        <div className="bg-white rounded-2xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#666]">Type</Label>
              <select value={form.method_type} onChange={(e) => setForm({...form, method_type: e.target.value})} className="mt-1 w-full h-10 rounded-md border border-[#0A192F]/20 px-3">
                <option value="upi">UPI</option>
                <option value="card">Card</option>
              </select>
            </div>
            <div><Label className="text-xs uppercase tracking-widest text-[#666]">Label</Label><Input value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} className="mt-1" placeholder="My HDFC card" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-[#666]">Details</Label><Input value={form.details} onChange={(e) => setForm({...form, details: e.target.value})} className="mt-1" placeholder={form.method_type === "upi" ? "name@upi" : "**** 1234"} /></div>
          </div>
          <div className="mt-4 flex gap-3">
            <button onClick={save} className="bg-[#E65A28] text-white px-6 py-2 rounded-full">Save</button>
            <button onClick={() => setShowForm(false)} className="border border-[#0A192F] text-[#0A192F] px-6 py-2 rounded-full">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowForm(true)} className="bg-[#0A192F] text-white px-6 py-3 rounded-full font-semibold" data-testid="add-pm-btn">+ Add payment method</button>
      )}
    </div>
  );
}
